import type { Translations } from "@/src/i18n/translations";

/**
 * Le délai que la modération se donne, en heures.
 *
 * ⚠️ **Ce n'est pas un réglage d'interface.** La directive App Store 1.2 impose
 * de traiter un contenu signalé sous 24 h, et c'est déjà elle qui fait trier la
 * file du serveur du **plus ancien au plus récent** (cf. `useReports`). Le
 * tableau de bord ne fait qu'en rendre l'échéance visible avant qu'elle tombe.
 */
export const SLA_HOURS = 24;

/**
 * L'alerte se lève à 18 h, pas à 24.
 *
 * Une garde qui n'apprend le dépassement qu'au moment où il se produit ne peut
 * plus rien en faire. Six heures d'avance, c'est le temps qu'il faut pour
 * qu'une relève voie le dossier avant qu'il ne soit trop tard.
 */
const WARN_RATIO = 0.75;

export type SlaLevel = "fresh" | "soon" | "late";

export type Sla = {
  /** Heures écoulées depuis le signalement. Jamais négatives. */
  hours: number;
  level: SlaLevel;
};

/**
 * L'âge d'un signalement, mesuré contre le délai de 24 h.
 *
 * Fonction **pure** : l'instant de référence est un argument, comme pour
 * `relativeTime` — c'est ce qui la rend testable sans figer l'horloge, et ce
 * qui empêche deux appels d'un même rendu de tomber sur des minutes
 * différentes.
 *
 * @param iso - date de dépôt du signalement, telle que le serveur la rend.
 * @param now - instant de référence, en millisecondes.
 * @returns `null` si la date est illisible : à l'appelant de décider quoi
 *   afficher plutôt que de laisser passer un `NaN` dans une jauge.
 */
export function sla(iso: string, now: number): Sla | null {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;

  // Un signalement daté du futur — horloges désaccordées entre le serveur et
  // le poste — vaut zéro : une jauge négative se dessinerait à l'envers.
  const hours = Math.max(0, (now - then) / 3_600_000);

  if (hours >= SLA_HOURS) return { hours, level: "late" };
  if (hours >= SLA_HOURS * WARN_RATIO) return { hours, level: "soon" };

  return { hours, level: "fresh" };
}

/**
 * L'attente d'un dossier, dite en toutes lettres : « 31 heures ».
 *
 * ⚠️ **Et non `relativeTime`**, qui dirait « hier » — sa forme `numeric: "auto"`
 * est un bienfait dans un fil de discussion et un défaut ici : sur une échéance
 * de 24 h, « hier » recouvre aussi bien 13 heures que 35, c'est-à-dire les deux
 * côtés de la limite. La file de modération se compte en heures, comme la
 * directive qui la régit.
 *
 * `Intl.NumberFormat` en style d'unité plutôt qu'une concaténation avec « h » :
 * c'est lui qui connaît l'accord et l'espace de chaque langue.
 */
export function slaDuration(hours: number, locale: string): string {
  const format = (value: number, unit: "day" | "hour" | "minute") =>
    new Intl.NumberFormat(locale, {
      style: "unit",
      unit,
      unitDisplay: "long",
    }).format(value);

  if (hours < 1) return format(Math.round(hours * 60), "minute");
  if (hours < 2 * SLA_HOURS) return format(Math.round(hours), "hour");

  // Au-delà de deux jours, l'heure exacte n'apprend plus rien : le dossier est
  // en retard depuis longtemps, c'est l'ordre de grandeur qui parle.
  return format(Math.round(hours / 24), "day");
}

type DashboardKey = keyof Translations["dashboard"];

/**
 * Les trois états, leur ton et leur libellé — **jamais la couleur seule**.
 *
 * Une jauge qui vire au rouge ne dit rien à qui ne distingue pas le rouge du
 * vert, ni à qui la lit dans une capture en noir et blanc. La pastille porte
 * donc toujours le mot, et `satisfies` fait échouer `tsc` si un état gagné plus
 * tard arrivait sans le sien.
 */
export const SLA_TONE = {
  fresh: "success",
  soon: "warning",
  late: "danger",
} as const satisfies Record<SlaLevel, "danger" | "success" | "warning">;

export const SLA_KEY = {
  fresh: "sla-fresh",
  soon: "sla-soon",
  late: "sla-late",
} as const satisfies Record<SlaLevel, DashboardKey>;
